import React from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { renderChart } from '@/utils/chartRenderer'

export const cleanChartContainersFromElement = (element: HTMLElement): void => {
  const chartContainers = element.querySelectorAll('.chart-container')
  chartContainers.forEach(chartContainer => chartContainer.innerHTML = '')
}

export const cleanChartContainersFromHTML = (content: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content
  cleanChartContainersFromElement(tempDiv)
  return tempDiv.innerHTML
}

export const getContentChartIds = (container: HTMLElement): string[] => {
  return Array.from(container.querySelectorAll('.chart-container')).map((element) => element.getAttribute('data-chart-id') || '')
}

export const createTrashButton = (chartContainer: HTMLElement, updateContentCallback: () => void): HTMLButtonElement => {
  const trashButton = document.createElement('button')
  trashButton.className = 'absolute top-2 right-2 p-1 bg-muted hover:bg-error/10 text-muted-foreground hover:text-error rounded-sm transition-colors z-10'
  trashButton.title = 'Remove chart'
  trashButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>`

  // Add click handler to remove the chart container
  trashButton.addEventListener('click', (e) => {
    e.stopPropagation()
    chartContainer.remove()
    // Update content to trigger chart removal handling
    updateContentCallback()
  })

  return trashButton
}

export const saveSelection = (editorRef: React.RefObject<HTMLDivElement | null>): Range | null => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !editorRef.current) {
    return null
  }

  const range = selection.getRangeAt(0)
  // Only save if the selection is within the editor
  if (editorRef.current.contains(range.commonAncestorContainer)) {
    return range.cloneRange()
  }
  return null
}

export const restoreSelection = (range: Range | null) => {
  if (!range) {
    return
  }

  const selection = window.getSelection()
  if (!selection) {
    return
  }

  try {
    selection.removeAllRanges()
    selection.addRange(range)
  } catch {
    // Range might be invalid, ignore
  }
}

export const insertNodeAtSelection = (node: Node, editorRef: React.RefObject<HTMLDivElement | null>) => {
  const selection = window.getSelection()
  if (!selection || !editorRef.current) {
    // Fallback to append if no selection
    editorRef.current?.appendChild(node)
    return
  }

  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0)
    // Ensure the range is within the editor
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      // Insert the node at the current selection
      range.insertNode(node)
      // Move cursor after the inserted node
      range.setStartAfter(node)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    } else {
      // Fallback to append if range is outside editor
      editorRef.current.appendChild(node)
    }
  } else {
    // Fallback to append if no range
    editorRef.current.appendChild(node)
  }
}

export const initializeChart = (
  id: string,
  chart: ChallengeService.Chart,
  editorRef: React.RefObject<HTMLDivElement | null>,
  readOnly: boolean,
  updateContent: () => void
): (() => void) | void  => {
  if (!chart.data) {
    return
  }
  const chartContainer = editorRef.current?.querySelector(`div.chart-container[data-chart-id="${id}"]`) as HTMLElement
  if (chartContainer) {
    if (!readOnly) {
      const trashButton = createTrashButton(chartContainer, updateContent)
      chartContainer.appendChild(trashButton)
    }

    const chartDescriptionElement = document.createElement('div')
    chartDescriptionElement.textContent = `${chart.query}`
    chartDescriptionElement.className = 'text-xs text-muted-foreground italic truncate max-w-[calc(100%-2rem)] font-mono'
    chartDescriptionElement.contentEditable = 'false'
    chartContainer.prepend(chartDescriptionElement)

    const canvasContainer = document.createElement('div')
    canvasContainer.className = `w-full ${chart.chartType === 'table' ? '' : 'h-64'}`
    chartContainer.appendChild(canvasContainer)
    chartContainer.contentEditable = 'false'
    const cleanup = renderChart(chart, canvasContainer)
    return () => {
      cleanup?.()
    }

  }
}

